/* global self, caches, Response, WebSocket */
const CACHE = 'audio-v1';

const LOG_PREFIX = 'sw.js';
self.addEventListener('install', (event) => {
  console.log(LOG_PREFIX, 'Installing service worker...');
  self.skipWaiting();
});

self.addEventListener('activate', (event) => {
  console.log(LOG_PREFIX, 'Activating service worker...');
  caches.open(CACHE).then((cache) => {
    cache.keys().then((keys) => {
      keys.forEach((key) => {
        cache.delete(key);
      });
    });
  });
  event.waitUntil(self.clients.claim());
});

function postDownloadCompleteMessage(message) {
  self.clients.matchAll().then((clients) => {
    clients.forEach((client) => {
      client.postMessage({
        type: 'download-complete',
        message: message,
      });
    });
  });
}

function postDebugLog(data) {
  self.clients.matchAll().then((clients) => {
    clients.forEach((client) => {
      client.postMessage({ type: 'debug-log', data });
    });
  });
}

const wantsAudio = (req) => {
  return /restapi\.(eu\.)?deepdub\.ai\/api\/v1\/tts\/.*/.test(req.url) || /devrestapi\.deepdub\.app\/api\/v1\/tts\/.*/.test(req.url);
};

// --- WebSocket connection pool ---

const WS_URLS = {
  dev: 'wss://wsapi.deepdub.ai/dev',
  prod: 'wss://wsapi.deepdub.ai/open',
};

/** @type {Map<string, { socket: WebSocket, ready: Promise<WebSocket> }>} */
const wsPool = new Map();

function getWsEnv(restUrl) {
  return restUrl.includes('devrestapi') ? 'dev' : 'prod';
}

function getWsUrl(env, apiKey) {
  return `${WS_URLS[env]}?x-api-key=${apiKey}`;
}

function getPooledSocket(env, apiKey) {
  const poolKey = `${env}:${apiKey}`;
  const existing = wsPool.get(poolKey);

  if (existing && existing.socket.readyState === WebSocket.OPEN) {
    return existing.ready;
  }

  if (existing && existing.socket.readyState === WebSocket.CONNECTING) {
    return existing.ready;
  }

  // Close stale socket if any
  if (existing) {
    try { existing.socket.close(); } catch (_) {}
    wsPool.delete(poolKey);
  }

  const socket = new WebSocket(getWsUrl(env, apiKey));
  const ready = new Promise((resolve, reject) => {
    socket.addEventListener('open', () => {
      postDebugLog({ event: 'ws-pool-connected', env });
      resolve(socket);
    });
    socket.addEventListener('error', () => {
      wsPool.delete(poolKey);
      reject(new Error('WebSocket connection failed'));
    });
  });

  socket.addEventListener('close', () => {
    wsPool.delete(poolKey);
  });

  wsPool.set(poolKey, { socket, ready });
  return ready;
}

/**
 * Send a generation request over WebSocket and collect MP3 chunks.
 * Returns an ArrayBuffer of the full MP3.
 */
async function generateViaWebSocket(env, apiKey, params) {
  const generationId = crypto.randomUUID();
  const messageToSend = {
    ...params,
    action: 'text-to-speech',
    generationId,
    format: 'mp3',
  };
  delete messageToSend.apiKey;
  delete messageToSend.cacheBust;

  let socket;
  try {
    socket = await getPooledSocket(env, apiKey);
  } catch (_) {
    postDebugLog({ event: 'ws-connect-retry', env });
    socket = await getPooledSocket(env, apiKey);
  }

  return new Promise((resolve, reject) => {
    const chunks = [];
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('WebSocket generation timed out'));
    }, 60_000);

    function onMessage(event) {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (_) {
        return;
      }

      if (data.generationId !== generationId) return;

      if (data.error) {
        cleanup();
        reject(data.errorType || data.error);
        return;
      }

      if (data.data) {
        const binary = base64ToBytes(data.data);
        chunks.push(binary);
      }

      if (data.isFinished) {
        cleanup();
        const total = chunks.reduce((s, c) => s + c.byteLength, 0);
        const result = new Uint8Array(total);
        let offset = 0;
        for (const chunk of chunks) {
          result.set(new Uint8Array(chunk), offset);
          offset += chunk.byteLength;
        }
        resolve(result.buffer);
      }
    }

    function cleanup() {
      clearTimeout(timeout);
      socket.removeEventListener('message', onMessage);
      socket.removeEventListener('close', onClose);
      socket.removeEventListener('error', onError);
    }

    function onClose() {
      cleanup();
      reject(new Error('WebSocket closed during generation'));
    }

    function onError() {
      cleanup();
      reject(new Error('WebSocket error during generation'));
    }

    socket.addEventListener('message', onMessage);
    socket.addEventListener('close', onClose);
    socket.addEventListener('error', onError);

    postDebugLog({ event: 'ws-generate-send', generationId });
    socket.send(JSON.stringify(messageToSend));
  });
}

function base64ToBytes(base64) {
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes.buffer;
}

// --- Fetch handler ---

self.addEventListener('fetch', (event) => {
  const request = event.request;

  if (!wantsAudio(request)) {
    return;
  }

  event.respondWith(handleAudioRequest(request));
});

async function handleAudioRequest(request) {
  const cache = await caches.open(CACHE);
  const cached = await cache.match(request);
  if (cached) {
    postDebugLog({ event: 'cache-hit', url: request.url });
    postDownloadCompleteMessage(request.url);
    return cached;
  }

  const url = new URL(request.url);
  const encodedParams = url.searchParams.get('params');
  if (!encodedParams) {
    return new Response('No params', { status: 400 });
  }
  const params = JSON.parse(encodedParams);

  if (params.realtime) {
    return handleWebSocketGeneration(request, params, cache);
  }

  return handleRestGeneration(request, params, url, cache);
}

async function handleWebSocketGeneration(request, params, cache) {
  const apiKey = params.apiKey;
  const env = getWsEnv(request.url);

  postDebugLog({ event: 'ws-generate-start', url: request.url });

  try {
    const audioBuffer = await generateViaWebSocket(env, apiKey, params);

    const response = new Response(audioBuffer, {
      headers: {
        'Content-Type': 'audio/mpeg',
        'Access-Control-Allow-Origin': '*',
      },
    });

    cache.put(request, response.clone());
    postDownloadCompleteMessage(request.url);
    postDebugLog({ event: 'ws-generate-ok', url: request.url, size: audioBuffer.byteLength });

    return response;
  } catch (err) {
    const message = typeof err === 'string' ? err : err?.message || 'WebSocket generation failed';
    postDebugLog({ event: 'ws-generate-error', url: request.url, error: message });

    return new Response(JSON.stringify({ success: false, message }), {
      status: 500,
      headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*' },
    });
  }
}

async function handleRestGeneration(request, params, url, cache) {
  const requestCopy = { ...request };
  const apiKey = params.apiKey;
  delete params.apiKey;
  url.searchParams.delete('params');
  delete requestCopy.url;

  const actualRequest = {
    ...requestCopy,
    method: 'POST',
    headers: {
      ...request.headers,
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
    },
    body: JSON.stringify(params),
  };

  const res = await fetch(url.toString().replace('/proxy', ''), actualRequest);
  const contentType = res.headers.get('content-type') || '';

  if (res.status === 200) {
    if (!contentType.includes('audio')) {
      const bodyText = await res.clone().text();
      postDebugLog({
        event: 'non-audio-200',
        url: request.url,
        contentType,
        bodyPreview: bodyText.substring(0, 500),
      });
      return new Response(bodyText, {
        status: res.status,
        headers: { 'Content-Type': contentType, 'Access-Control-Allow-Origin': '*' },
      });
    }

    postDebugLog({ event: 'tts-response-ok', url: request.url, contentType });

    const resCopy = res.clone();
    const resCopy4Cache = res.clone();

    resCopy.bytes().then(() => {
      cache.put(request, resCopy4Cache);
      postDownloadCompleteMessage(request.url);
    });
    return new Response(res.body, {
      headers: {
        'Content-Type': 'audio/mpeg',
        'Access-Control-Allow-Origin': '*',
      },
    });
  } else {
    const bodyText = await res.clone().text();
    postDebugLog({
      event: 'tts-error',
      url: request.url,
      status: res.status,
      contentType,
      bodyPreview: bodyText.substring(0, 500),
    });
    return new Response(res.body, { status: res.status });
  }
}
